import React, { useState, useEffect, useRef } from 'react';
import { FolderOpen, Upload, Trash2, Download, Car, CreditCard, FileText, FolderArchive, Plus, User, GraduationCap, AlertTriangle, CheckCircle, RefreshCw, Sparkles, ShieldCheck, ShieldX, ChevronDown, Truck, Gauge } from 'lucide-react';
import { useConfirm } from '../components/ConfirmModal';
import { pdfToImage, isPdf, compressForGroq } from '../utils/pdfToImage';

const API = 'https://qbdp-backend-production.up.railway.app/api';

const CATEGORIES = [
  { key: 'permis',           label: 'Permis de conduire',    Icon: Car,            color: '#7c3aed', bg: '#f5f3ff' },
  { key: 'carte_conducteur', label: 'Carte de conducteur',   Icon: Gauge,          color: '#0e7490', bg: '#ecfeff' },
  { key: 'cqc',             label: 'CQC',                    Icon: Truck,          color: '#b45309', bg: '#fffbeb' },
  { key: 'identite',        label: "Pièce d'identité",       Icon: CreditCard,     color: '#0891b2', bg: '#ecfeff' },
  { key: 'contrat',         label: 'Contrat',                Icon: FileText,       color: '#16a34a', bg: '#f0fdf4' },
  { key: 'diplome',         label: 'Diplôme',                Icon: GraduationCap,  color: '#1a56db', bg: '#eff6ff' },
  { key: 'administratif',   label: 'Administratif',          Icon: FolderArchive,  color: '#d97706', bg: '#fffbeb' },
];

const ACCEPT = '.pdf,.jpg,.jpeg,.png,.gif,.webp,.heic,.doc,.docx,.xls,.xlsx,.odt,.ods';

const ALERTE_STYLE = {
  danger:  { bg: '#fef2f2', color: '#dc2626', border: '#fecaca', label: 'Alerte' },
  warning: { bg: '#fffbeb', color: '#d97706', border: '#fde68a', label: 'Attention' },
  ok:      { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0', label: 'Valide' },
  unknown: { bg: '#f9fafb', color: '#6b7280', border: '#e5e7eb', label: 'À vérifier' },
};

function fmtSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
}

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function AuthenticiteBadge({ details }) {
  if (!details?.authenticite || details.authenticite === 'inconnu') return null;
  const suspect = details.authenticite === 'suspect';
  return (
    <div title={details.motif_suspect || ''} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 8,
      background: suspect ? '#fef2f2' : '#f0fdf4', color: suspect ? '#dc2626' : '#16a34a',
      border: `1px solid ${suspect ? '#fecaca' : '#bbf7d0'}`, fontSize: 10, fontWeight: 700, cursor: suspect ? 'help' : 'default', flexShrink: 0 }}>
      {suspect ? <ShieldX size={10} /> : <ShieldCheck size={10} />}
      {suspect ? 'Suspect' : 'Authentique'}
    </div>
  );
}

function AlerteBadge({ alerte, resume }) {
  if (!alerte) return null;
  const s = ALERTE_STYLE[alerte] || ALERTE_STYLE.unknown;
  return (
    <div title={resume || ''} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 8,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`, fontSize: 10, fontWeight: 700, cursor: 'help', flexShrink: 0 }}>
      {alerte === 'danger' ? <AlertTriangle size={10} /> : alerte === 'ok' ? <CheckCircle size={10} /> : <AlertTriangle size={10} />}
      {s.label}
    </div>
  );
}

export default function DocumentsEmployes() {
  const [employes, setEmployes]         = useState([]);
  const [selectedEmp, setSelectedEmp]   = useState(null);
  const [docs, setDocs]                 = useState([]);
  const [uploading, setUploading]       = useState(null);
  const [analysing, setAnalysing]       = useState(new Set());
  const [toast, setToast]               = useState(null);
  const { showConfirm, confirmModal }   = useConfirm();
  const [empSearch, setEmpSearch]       = useState('');
  const [empSelectorOpen, setEmpSelectorOpen] = useState(false);
  const empSelectorRef                  = useRef(null);
  const token = sessionStorage.getItem('qbdp_token');

  useEffect(() => {
    if (!empSelectorOpen) return;
    const handler = (e) => {
      if (empSelectorRef.current && !empSelectorRef.current.contains(e.target))
        setEmpSelectorOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [empSelectorOpen]);

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    fetch(`${API}/employes`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        const list = (Array.isArray(d) ? d : (d.employes || []))
          .sort((a, b) => (a.nom || '').localeCompare(b.nom || '', 'fr', { sensitivity: 'base' }));
        setEmployes(list);
        if (list.length > 0) setSelectedEmp(list[0]);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedEmp) return;
    fetch(`${API}/documents-employes?employe_id=${selectedEmp.id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setDocs(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [selectedEmp]);

  const fetchDoc = async (docId) => {
    const r = await fetch(`${API}/documents-employes?employe_id=${selectedEmp.id}`, { headers: { Authorization: `Bearer ${token}` } });
    const d = await r.json();
    return (Array.isArray(d) ? d : []).find(x => x.id === docId) || null;
  };

  const rechargerDoc = async (docId) => {
    const updated = await fetchDoc(docId).catch(() => null);
    if (updated) setDocs(prev => prev.map(p => p.id === docId ? updated : p));
  };

  const analyserDoc = async (docId) => {
    setAnalysing(prev => new Set([...prev, docId]));
    try {
      for (let i = 0; i < 10; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const updated = await fetchDoc(docId).catch(() => null);
        if (updated) {
          setDocs(prev => prev.map(p => p.id === docId ? updated : p));
          if (updated.ai_alerte !== null) break;
        }
      }
    } finally {
      setAnalysing(prev => { const s = new Set(prev); s.delete(docId); return s; });
    }
  };

  const relancerAnalyse = async (docId) => {
    setAnalysing(prev => new Set([...prev, docId]));
    try {
      const r = await fetch(`${API}/documents-employes/${docId}/analyser`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const result = await r.json();
      await rechargerDoc(docId);
      if (result.alerte) showToast(`Analyse : ${result.alerte} — ${result.resume || ''}`);
      else showToast(result.resume || result.error || 'Analyse terminée (aucun résultat)', result.alerte !== undefined);
    } catch (e) { showToast(`Erreur analyse : ${e.message}`, false); }
    finally { setAnalysing(prev => { const s = new Set(prev); s.delete(docId); return s; }); }
  };

  const handleUpload = async (categorie, file) => {
    if (!file || !selectedEmp) return;
    if (file.size > 15 * 1024 * 1024) { showToast('Fichier trop lourd (max 15 Mo)', false); return; }
    setUploading(categorie);
    try {
      let nom = file.name;
      let mime = file.type;
      let data;

      if (isPdf(file)) {
        showToast('Conversion PDF en cours…');
        const converted = await pdfToImage(file);
        data = converted.data;
        mime = converted.mime;
        nom = converted.nom;
      } else {
        data = await new Promise((res, rej) => {
          const reader = new FileReader();
          reader.onload = () => res(reader.result);
          reader.onerror = rej;
          reader.readAsDataURL(file);
        });
      }

      // Compression si image trop grande pour Groq (limite 4MB base64)
      if (mime && mime.startsWith('image/')) {
        data = await compressForGroq(data);
        mime = 'image/jpeg';
      }

      const r = await fetch(`${API}/documents-employes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ employe_id: selectedEmp.id, categorie, nom, mime, data }),
      });
      const d = await r.json();
      if (d.id) {
        setDocs(prev => [d, ...prev]);
        showToast('Document ajouté — analyse IA en cours…');
        analyserDoc(d.id);
      } else showToast(d.error || 'Erreur', false);
    } catch (err) { showToast(err.message || 'Erreur réseau', false); }
    setUploading(null);
  };

  const handleDelete = async (id, nom) => {
    const ok = await showConfirm({
      title: 'SUPPRIMER CE DOCUMENT',
      subtitle: nom,
      body: 'Cette action est irréversible. Le document sera définitivement supprimé.',
      confirmLabel: 'Supprimer',
      confirmColor: '#dc2626',
      icon: Trash2,
    });
    if (!ok) return;
    await fetch(`${API}/documents-employes/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    setDocs(prev => prev.filter(d => d.id !== id));
    showToast('Document supprimé');
  };

  const handleDownload = async (doc) => {
    try {
      const r = await fetch(`${API}/documents-employes/${doc.id}/data`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      const a = document.createElement('a'); a.href = d.data; a.download = d.nom; a.click();
    } catch { showToast('Erreur téléchargement', false); }
  };

  const initiales = (emp) => {
    if (!emp?.nom) return '?';
    return emp.nom.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  };

  // Alertes globales (danger + warning)
  const alertesGlobales = docs.filter(d => d.ai_alerte === 'danger' || d.ai_alerte === 'warning')
    .sort((a, b) => (a.ai_alerte === 'danger' ? 0 : 1) - (b.ai_alerte === 'danger' ? 0 : 1));

  return (
    <div style={{ padding: 32, maxWidth: 1060, margin: '0 auto' }}>

      {toast && (
        <div style={{ position: 'fixed', top: 24, right: 24, zIndex: 2000, padding: '12px 20px', borderRadius: 12,
          background: toast.ok ? '#f0fdf4' : '#fef2f2', color: toast.ok ? '#16a34a' : '#dc2626',
          border: `1px solid ${toast.ok ? '#bbf7d0' : '#fecaca'}`, fontWeight: 600, fontSize: 13,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1a1a2e', margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          <FolderOpen size={22} /> Documents employés
        </h1>
        <p style={{ fontSize: 13, color: '#6b7280', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
          Coffre-fort numérique — <Sparkles size={12} color="#d97706" /> analyse IA automatique des documents images
        </p>
      </div>

      {/* Panel alertes IA */}
      {alertesGlobales.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #fecaca', borderRadius: 14, padding: '16px 20px', marginBottom: 20, borderLeft: '4px solid #dc2626' }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#dc2626', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
            <AlertTriangle size={14} /> {alertesGlobales.length} alerte{alertesGlobales.length > 1 ? 's' : ''} IA détectée{alertesGlobales.length > 1 ? 's' : ''}
          </div>
          {alertesGlobales.map(d => {
            const cat = CATEGORIES.find(c => c.key === d.categorie);
            const s = ALERTE_STYLE[d.ai_alerte];
            const details = d.ai_details;
            return (
              <div key={d.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderBottom: '1px solid #fef2f2' }}>
                <AlerteBadge alerte={d.ai_alerte} />
                {details?.authenticite === 'suspect' && <AuthenticiteBadge details={details} />}
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{cat?.label} — <span style={{ color: '#6b7280', fontWeight: 400 }}>{d.nom}</span></div>
                  {d.ai_resume && <div style={{ fontSize: 11, color: s?.color || '#6b7280', marginTop: 2 }}>{d.ai_resume}</div>}
                  {details?.motif_suspect && <div style={{ fontSize: 11, color: '#dc2626', marginTop: 2 }}>⚠️ {details.motif_suspect}</div>}
                  {d.date_expiration && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>Expiration : {fmtDate(d.date_expiration)}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Sélecteur employé — dropdown searchable */}
      <div ref={empSelectorRef} style={{ position: 'relative', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, marginBottom: 24 }}>
        <button
          onClick={() => { setEmpSelectorOpen(o => !o); setEmpSearch(''); }}
          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
            background: 'none', border: 'none', cursor: 'pointer', borderRadius: 14 }}>
          <User size={13} color="#374151" />
          {selectedEmp ? (
            <>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--blue)', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                {initiales(selectedEmp)}
              </div>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e', flex: 1, textAlign: 'left' }}>{selectedEmp.nom}</span>
              {selectedEmp.service && <span style={{ fontSize: 11, color: '#9ca3af' }}>{selectedEmp.service}</span>}
            </>
          ) : (
            <span style={{ fontSize: 13, color: '#9ca3af', flex: 1, textAlign: 'left' }}>Sélectionner un employé</span>
          )}
          <span style={{ fontSize: 11, color: '#9ca3af', background: '#f3f4f6', borderRadius: 20, padding: '2px 8px', flexShrink: 0 }}>{employes.length}</span>
          <ChevronDown size={14} color="#9ca3af" style={{ transform: empSelectorOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
        </button>
        {empSelectorOpen && (
          <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 200,
            background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden' }}>
            <div style={{ padding: '8px 12px', borderBottom: '1px solid #f3f4f6' }}>
              <input
                autoFocus
                value={empSearch}
                onChange={e => setEmpSearch(e.target.value)}
                placeholder="Rechercher…"
                style={{ width: '100%', border: 'none', outline: 'none', fontSize: 13, color: '#374151', background: 'transparent' }}
              />
            </div>
            <div style={{ maxHeight: 240, overflowY: 'auto' }}>
              {employes
                .filter(e => !empSearch || e.nom.toLowerCase().includes(empSearch.toLowerCase()))
                .map(emp => {
                  const active = selectedEmp?.id === emp.id;
                  return (
                    <button key={emp.id} onClick={() => { setSelectedEmp(emp); setEmpSelectorOpen(false); }}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
                        background: active ? '#eff6ff' : 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: active ? 'var(--blue)' : '#e5e7eb',
                        color: active ? '#fff' : '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                        {initiales(emp)}
                      </div>
                      <span style={{ fontSize: 13, fontWeight: active ? 700 : 400, color: active ? 'var(--blue)' : '#374151', flex: 1 }}>{emp.nom}</span>
                      {emp.service && <span style={{ fontSize: 11, color: '#9ca3af' }}>{emp.service}</span>}
                    </button>
                  );
                })}
              {employes.filter(e => !empSearch || e.nom.toLowerCase().includes(empSearch.toLowerCase())).length === 0 && (
                <div style={{ padding: '12px 16px', fontSize: 13, color: '#9ca3af', textAlign: 'center' }}>Aucun résultat</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Grille 5 catégories */}
      {selectedEmp && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {CATEGORIES.map(({ key, label, Icon, color, bg }) => {
            const catDocs = docs.filter(d => d.categorie === key);
            const fileRef = React.createRef();
            return (
              <div key={key} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, overflow: 'hidden' }}>
                {/* En-tête */}
                <div style={{ background: bg, padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e5e7eb' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon size={18} color="white" />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>{label}</div>
                      <div style={{ fontSize: 11, color: '#6b7280' }}>{catDocs.length} document{catDocs.length !== 1 ? 's' : ''}</div>
                    </div>
                  </div>
                  <button onClick={() => fileRef.current?.click()} disabled={uploading === key}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8,
                      border: 'none', background: color, color: '#fff', fontSize: 12, fontWeight: 600,
                      cursor: uploading === key ? 'default' : 'pointer', opacity: uploading === key ? 0.6 : 1 }}>
                    {uploading === key ? <><Upload size={13} /> Envoi...</> : <><Plus size={13} /> Ajouter</>}
                  </button>
                  <input ref={fileRef} type="file" accept={ACCEPT} style={{ display: 'none' }}
                    onChange={e => { handleUpload(key, e.target.files[0]); e.target.value = ''; }} />
                </div>

                {/* Liste documents */}
                <div style={{ padding: catDocs.length ? 0 : 20 }}>
                  {catDocs.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 12, padding: '24px 0' }}>Aucun document</div>
                  ) : (
                    catDocs.map((doc, i) => {
                      const details = doc.ai_details;
                      return (
                      <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
                        borderBottom: i < catDocs.length - 1 ? '1px solid #f3f4f6' : 'none', background: '#fff',
                        borderLeft: details?.authenticite === 'suspect' ? '3px solid #dc2626' : 'none' }}>
                        <FileText size={15} color={color} style={{ flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 500, color: '#1a1a2e', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {doc.nom}
                          </div>
                          <div style={{ fontSize: 10, color: '#9ca3af' }}>
                            {fmtDate(doc.created_at)}{doc.taille ? ` · ${fmtSize(doc.taille)}` : ''}
                            {doc.date_expiration && <span style={{ color: doc.ai_alerte === 'danger' ? '#dc2626' : '#d97706', marginLeft: 6 }}>Exp. {fmtDate(doc.date_expiration)}</span>}
                          </div>
                          {doc.ai_resume && (
                            <div style={{ fontSize: 10, color: ALERTE_STYLE[doc.ai_alerte]?.color || '#6b7280', marginTop: 2, fontStyle: 'italic' }}>
                              {doc.ai_resume}
                            </div>
                          )}
                          {details?.motif_suspect && (
                            <div style={{ fontSize: 10, color: '#dc2626', marginTop: 2, fontWeight: 600 }}>
                              ⚠️ DOCUMENT SUSPECT : {details.motif_suspect}
                            </div>
                          )}
                        </div>
                        {analysing.has(doc.id) ? (
                          <div title="Analyse IA en cours…" style={{ padding: '5px 6px', display: 'flex', alignItems: 'center', opacity: 0.6 }}>
                            <RefreshCw size={12} color="#d97706" style={{ animation: 'spin 1s linear infinite' }} />
                          </div>
                        ) : (
                          <>
                            <AlerteBadge alerte={doc.ai_alerte} resume={doc.ai_resume} />
                            <AuthenticiteBadge details={details} />
                          </>
                        )}
                        <button onClick={() => relancerAnalyse(doc.id)} title="Relancer l'analyse IA"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '3px', opacity: 0.5 }}>
                          <Sparkles size={11} color="#d97706" />
                        </button>
                        <button onClick={() => handleDownload(doc)} title="Télécharger"
                          style={{ background: '#f3f4f6', border: 'none', borderRadius: 7, padding: '5px 7px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                          <Download size={12} color="#374151" />
                        </button>
                        <button onClick={() => handleDelete(doc.id, doc.nom)} title="Supprimer"
                          style={{ background: '#fef2f2', border: 'none', borderRadius: 7, padding: '5px 7px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                          <Trash2 size={12} color="#dc2626" />
                        </button>
                      </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
      {confirmModal}
    </div>
  );
}
